import React, { memo } from 'react';
import InterventionsCard from './InterventionsCard';
import useIntervention from 'src/hooks/api/useIntervention';
import Intervention from 'src/interfaces/api/Intervention';
import { Order } from 'src/interfaces/Table';
import { useRecoilState, useSetRecoilState } from 'recoil';
import interventionsState, { InterventionState } from 'src/recoil/atoms/ticketInterventions';
import employeesState from 'src/recoil/atoms/employees';
import useEmployeeAccess from 'src/hooks/api/useEmployeeAccess';
import { unique } from 'src/utils/ArrayUtils';
import { CollectionResponse } from 'src/interfaces/api/api';

const InterventionsCardContainer = ({ ticket, hidden }) => {
  const { getAll, remove: removeIntervention } = useIntervention();
  const setInterventions = useSetRecoilState(interventionsState);

  const { getByIds: getEmployees } = useEmployeeAccess();
  const [employees, setEmployees] = useRecoilState(employeesState);

  const fetchInterventionsEmployees = async (interventions: Intervention[]) => {
    const cachedIds = Object.keys(employees);

    let employeesIds = unique(interventions.map((intervention) => intervention.employeeId));
    employeesIds = employeesIds.filter((id) => !cachedIds.includes(id));

    if (employeesIds.length > 0) {
      const collection = await getEmployees(employeesIds);
      const newEmployees = collection.items.map((employee) => ({ [employee.id]: employee }));

      setEmployees((employees) => Object.assign({}, employees, ...newEmployees));
    }
  };

  const handleGetAllInterventions = async (
    page: number,
    limit: number,
    orderBy: keyof InterventionState,
    order: Order
  ) => {
    const collection = await getAll(ticket.id, page, limit, orderBy as keyof Intervention, order);

    fetchInterventionsEmployees(collection.items);

    collection.items = collection.items.map((intervention) => ({ ...intervention, ticketId: ticket.id }));

    return collection as CollectionResponse<InterventionState>;
  };

  const handleRemoveIntervention = async (id: Intervention['id']) => {
    const response = await removeIntervention(id);

    setInterventions((interventions) => {
      const newInterventions = Object.values(interventions)
        .filter((intervention) => intervention.id !== id)
        .map((intervention) => ({ [intervention.id]: intervention }));

      return Object.assign({}, ...newInterventions);
    });

    return response;
  };

  if (hidden) {
    return null;
  }

  return (
    <InterventionsCard onRemoveIntervention={handleRemoveIntervention} onGetInterventions={handleGetAllInterventions} />
  );
};

export default memo(
  InterventionsCardContainer,
  (prevProps, nextProps) => prevProps.ticket?.id === nextProps.ticket?.id && prevProps.hidden === nextProps.hidden
);
